import React, { useState, useRef, useCallback, useEffect } from 'react';

const FADE_MS = 350;

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
}

const cloudFront = "https://d2s5ye0agxvg00.cloudfront.net";

const images = [
   { src: `${cloudFront}/praxinoscope.mov`, date: '2026-06-17', description: 'Visual created using a Blender model of a praxinoscope and a custom edge detection shader.' },
  { src: `${cloudFront}/spine.mp4`, date: '2026-06-12', description: 'Visual created using real MRI data (from a spine) downloaded from openneuro.' },
  { src: `${cloudFront}/dancer.mp4`, date: '2026-06-05', description: 'The dancer: light dispersion effect created using a custom WebGL shader overlaid atop video footage.' },
  { src: `${cloudFront}/beware.webp`, date: '2026-05-23', description: 'Little PSA using public domain imagery.' },
  { src: `${cloudFront}/ginger.webp`, date: '2026-05-15', description: 'Found this piece of ginger in the back of my pantry. Strange, yearning things are living among us.' },
  { src: `${cloudFront}/mosaic_short.mov`, date: '2026-04-26', description: 'Mosaic effect built in WebGL: sampling a texture using a custom voronoi partitioning shader.' },
   { src: `${cloudFront}/interference_2.webp`, date: '2026-04-22', description: 'Playing with interference pattern effects using a web-based raytracer.' },
  { src: `${cloudFront}/Particles.mp4`, date: '2026-03-10', description: 'Science fiction: exploring particle systems and having fun with the pipeline of GLSL --> GIF.' },
  { src: `${cloudFront}/message.webp`, date: '2026-03-04', description: 'The medium is the message...' },
  { src: `${cloudFront}/moon viewing party.webp`, date: '2026-03-02', description: 'Thinking about line drawings, modern depictions of femininity, online lit mags, witchiness.' },
  { src: `${cloudFront}/veggies.webp`, date: '2026-02-10', description: 'Inspired by line art styles I saw in and around the punk/indie scene growing up (e.g. merch shirts, tattoos). Eat your vegetables!' },
  { src: `${cloudFront}/BALLOONS.mp4`, date: '2026-02-03', description: 'GIF of a short video I filmed celebrating one of the most playful, powerful, and generative forces on earth: wind.' },
  { src: `${cloudFront}/duotone.webp`, date: '2026-01-17', description: "Horseshoe crabs were a classic sighting at the Jersey shore where I grew up. Here's one of the little wanderers in duotone." },
  { src: `${cloudFront}/focus.mp4`, date: '2026-01-28', description: 'Adventures in optometry: the animation.' },
  { src: `${cloudFront}/abc.webp`, date: '2026-01-22', description: 'Dr. Seuss-inspired lettering + a grinchy green.' },
  { src: `${cloudFront}/magical thinking.webp`, date: '2025-12-25', description: 'Some older work, though I still have a soft spot for magical thinking...' },
  { src: `${cloudFront}/attention.mp4`, date: '2025-12-18', description: 'Need I say more?' },
  { src: `${cloudFront}/teeth.mp4`, date: '2025-12-30', description: 'Teeeeeeeth.' },
];

export default function PostersScroll() {
  const width = useWindowWidth();
  const isMobile = width <= 1200;
  const isTablet = width > 600 && width <= 1024;

  const containerWidth = isMobile ? '90%' : isTablet ? '90%' : '85%';
  const containerMarginLeft = isMobile ? '3%' : isTablet ? '5%' : '0%';
  const introWidth = isMobile ? '90%' : isTablet ? '70%' : '30%';
  const introMarginLeft = isMobile ? '5%' : '8rem';
  const introFontSize = isMobile ? 15 : 16;

  const skipEntranceAnimation = sessionStorage.getItem('postersScrollAnimated') === 'true';
  const [entered, setEntered] = useState(skipEntranceAnimation);

  useEffect(() => {
    sessionStorage.setItem('postersScrollAnimated', 'true');
    if (skipEntranceAnimation) return;
    const timer = setTimeout(() => setEntered(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const currentIndexRef = useRef(0);
  const cooldownRef = useRef(false);

  const navigate = useCallback((direction) => {
    if (cooldownRef.current) return;
    const nextIndex = currentIndexRef.current + direction;
    if (nextIndex < 0 || nextIndex >= images.length) return;

    cooldownRef.current = true;

    setVisible(false);
    setTimeout(() => {
      currentIndexRef.current = nextIndex;
      setCurrentIndex(nextIndex);
      setVisible(true);
      setTimeout(() => {
        cooldownRef.current = false;
      }, FADE_MS + 150);
    }, FADE_MS);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') navigate(1);
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') navigate(-1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigate]);

  const current = images[currentIndex];
  const isVideo = /\.(mp4|webm|mov)$/i.test(current.src);
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < images.length - 1;

  const formattedDate = new Date(current.date + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <>
      <style>{`
        .ps-nav-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 14px;
          color: #ccccccbc;
          padding: 0.4rem;
          line-height: 1;
          transition: color 0.2s ease;
          font-family: inherit;
        }
        .ps-nav-btn:hover:not(:disabled) {
          color: #555;
        }
        .ps-nav-btn:disabled {
          opacity: 0.2;
          cursor: default;
        }
        @keyframes psArrowBounce {
          0%, 100% { transform: translateY(0); opacity: 0.45; }
          50%       { transform: translateY(5px); opacity: 0.9; }
        }
      `}</style>

      {/* Intro */}
      <div style={{
        width: containerWidth,
        marginLeft: containerMarginLeft,
        marginTop: isMobile ? '8%' : '5%',
        marginBottom: '0rem',
        opacity: entered ? 1 : 0,
        transform: `translateY(${entered ? 0 : '20px'})`,
        transition: 'opacity 0.8s ease, transform 0.8s ease',
      }}>
        <p style={{
          fontSize: introFontSize,
          width: introWidth,
          marginLeft: introMarginLeft,
          lineHeight: 1.5,
          marginTop: 0,
          marginBottom: 0,
        }}>
          Each morning I try to sit down and make something. The idea isn't to generate a
          perfectly polished product, but to set aside some time for visual exploration, for play without expectation or judgement.
          On the best days I discover a new technique or start to scratch the surface of a bigger idea.
          <br /><br />
          Below is a sample of this work. The purpose of this section is to serve as something like a sketchbook,
          and lean into the idea of process over perfection.
        </p>

      </div>

      {/* Scroll viewer */}
      <div
        style={{
          position: 'relative',
          height: '100vh',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          userSelect: 'none',
          boxSizing: 'border-box',
          marginLeft: containerMarginLeft,
          opacity: entered ? 1 : 0,
          transform: `translateY(${entered ? 0 : '20px'})`,
          transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
        }}
      >

        {/* Left: media */}
        <div style={{
          width: isMobile ? '100%' : '50%',
          height: isMobile ? '58%' : '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? `2rem 1.5rem 0.5rem ${introMarginLeft}` : `3rem 2rem 3rem ${introMarginLeft}`,
          boxSizing: 'border-box',
        }}>
          <div style={{
            opacity: visible ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease`,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}>
            {isVideo ? (
              <video
                key={current.src}
                src={current.src}
                autoPlay
                muted
                loop
                playsInline
                style={{
                  maxHeight: isMobile ? '50vh' : '80vh',
                  maxWidth: '95%',
                  display: 'block',
                }}
              />
            ) : (
              <img
                key={current.src}
                src={current.src}
                alt=""
                style={{
                  maxHeight: isMobile ? '50vh' : '80vh',
                  maxWidth: '95%',
                  display: 'block',
                }}
              />
            )}
          </div>
        </div>

        {/* Right: description + nav */}
        <div style={{
          width: isMobile ? '100%' : '42%',
          height: isMobile ? '42%' : '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: isMobile ? '1rem 3rem 1.5rem' : '3rem 4rem 3rem 1rem',
          boxSizing: 'border-box',
        }}>
          {/* Top half — description anchored to its bottom edge */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            paddingBottom: '2rem',
            overflow: 'hidden',
          }}>
            <div style={{
              opacity: visible ? 1 : 0,
              transition: `opacity ${FADE_MS}ms ease`,
            }}>
              <p style={{
                fontSize: isMobile ? 14 : 15,
                lineHeight: 1.7,
                color: '#333',
                margin: '0 0 1.25rem 0',
                fontFamily: 'inherit',
              }}>
                {current.description}
              </p>
              <p style={{
                fontSize: isMobile ? 11 : 12,
                color: '#bbb',
                margin: 0,
                fontFamily: 'inherit',
                letterSpacing: '0.4px',
              }}>
                {formattedDate}
              </p>
            </div>
          </div>

          {/* Bottom half — nav anchored to its top edge (= center of right panel) */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'left',
            gap: isMobile ? '1.5rem' : '0.75rem',
          }}>
            <button
              className="ps-nav-btn"
              onClick={() => navigate(-1)}
              disabled={!canPrev}
              aria-label="Previous"
              style={isMobile ? { fontSize: 26, padding: '0.65rem 0.75rem' } : {}}
            >
              ↑
            </button>
            <span style={{ fontSize: 11, color: '#ccc', letterSpacing: '0.5px', fontFamily: 'inherit' }}>
              {currentIndex + 1} / {images.length}
            </span>
            <button
              className="ps-nav-btn"
              onClick={() => navigate(1)}
              disabled={!canNext}
              aria-label="Next"
              style={isMobile ? { fontSize: 26, padding: '0.65rem 0.75rem' } : {}}
            >
              ↓
            </button>
          </div>
          </div>
        </div>

      </div>
    </>
  );
}
